import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  Chip,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { onCreateShipment, fetchSingleOrder } from "../../redux/homeAction";

const OrderDetailedAudit = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isShipping, setIsShipping] = useState(false);
  const [shipData, setShipData] = useState({
    weight: "0.5",
    length: "15",
    breadth: "15",
    height: "10",
  });

  useEffect(() => {
    dispatch(fetchSingleOrder(id, setOrder, setLoading));
  }, [id, dispatch]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <Typography variant="h6" style={{ fontWeight: 700, color: "#334155" }}>
          Order record not found or expired.
        </Typography>
        <Button
          onClick={() => navigate(-1)}
          style={{ marginTop: "16px", textTransform: "none" }}
          variant="contained"
        >
          Back to Orders
        </Button>
      </div>
    );
  }

  const handleShipDataChange = (e) => {
    setShipData({ ...shipData, [e.target.name]: e.target.value });
  };

  const handleCreateShipment = async (e) => {
    e.preventDefault();
    setIsShipping(true);
    try {
      dispatch(
        onCreateShipment(shipData, order, setIsShipping, navigate, Swal),
      );
    } catch (err) {
      Swal.fire("Error", "Failed to create shipment", "error");
    } finally {
      setIsShipping(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "info";
      case "shipped":
        return "primary";
      case "delivered":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <div
      style={{
        padding: "32px",
        backgroundColor: "#f1f5f9",
        minHeight: "100vh",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* TOP HEADER BAR */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <Button
            onClick={() => navigate(-1)}
            style={{
              color: "#475569",
              textTransform: "none",
              fontWeight: 600,
              padding: 0,
              marginBottom: "4px",
            }}
          >
            ← Back to All Orders
          </Button>
          <Typography
            variant="h4"
            style={{ fontWeight: 800, color: "#0f172a" }}
          >
            Order #{order.orderId}
          </Typography>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <Chip
            label={`Status: ${order.status?.toUpperCase()}`}
            color={getStatusColor(order.status)}
            style={{ fontWeight: 700, borderRadius: "6px" }}
          />
          <Chip
            label={`Payment: ${order.payment?.method} (${order.payment?.status?.toUpperCase()})`}
            color={order.payment?.status === "paid" ? "success" : "warning"}
            variant="outlined"
            style={{ fontWeight: 700, borderRadius: "6px" }}
          />
        </div>
      </div>

      <Grid container spacing={3}>
        {/* LEFT COLUMN: PRODUCTS & CUSTOMER INFO */}
        <Grid item xs={12} lg={8}>
          {/* PRODUCT ITEMS LIST */}
          <Paper
            style={{
              padding: "24px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow: "none",
              marginBottom: "24px",
            }}
          >
            <Typography
              variant="h6"
              style={{
                fontWeight: 800,
                color: "#1e293b",
                marginBottom: "16px",
              }}
            >
              Ordered Items ({order.products?.length})
            </Typography>

            {order.products?.map((prod, index) => {
              const frontImg =
                prod.images?.find((img) => img.type === "front") ||
                prod.images?.[0];
              const imgUrl =
                frontImg?.urls?.medium || frontImg?.urls?.thumb || "";

              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    gap: "16px",
                    padding: "12px 0",
                    borderBottom:
                      index !== order.products.length - 1
                        ? "1px solid #f1f5f9"
                        : "none",
                    alignItems: "center",
                  }}
                >
                  {/* Thumbnail */}
                  <div
                    style={{
                      width: "60px",
                      height: "75px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "6px",
                      overflow: "hidden",
                      border: "1px solid #e2e8f0",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {imgUrl ? (
                      <img
                        src={imgUrl}
                        alt={prod.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: "9px", color: "#94a3b8" }}>
                        No Image
                      </span>
                    )}
                  </div>

                  {/* Details */}
                  <div style={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      style={{ fontWeight: 700, color: "#0f172a" }}
                    >
                      {prod.name}
                    </Typography>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        marginTop: "4px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          backgroundColor: "#f1f5f9",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          color: "#475569",
                        }}
                      >
                        SKU: {prod.sku}
                      </span>
                      <span
                        style={{
                          fontSize: "11px",
                          backgroundColor: "#eff6ff",
                          color: "#1d4ed8",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontWeight: 600,
                        }}
                      >
                        Size: {prod.size}
                      </span>
                      <span
                        style={{
                          fontSize: "11px",
                          backgroundColor: "#faf5ff",
                          color: "#6b21a8",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontWeight: 600,
                        }}
                      >
                        Color: {prod.color}
                      </span>
                      <span
                        style={{
                          fontSize: "11px",
                          backgroundColor:
                            prod.status === "cancelled" ? "#fee2e2" : "#f0fdf4",
                          color:
                            prod.status === "cancelled" ? "#991b1b" : "#166534",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontWeight: 600,
                        }}
                      >
                        {prod.status}
                      </span>
                    </div>
                    {prod.cancellationReason && (
                      <Typography
                        variant="caption"
                        style={{
                          color: "#dc2626",
                          display: "block",
                          marginTop: "2px",
                        }}
                      >
                        Reason: {prod.cancellationReason}
                      </Typography>
                    )}
                  </div>

                  {/* Price */}
                  <div style={{ textAlign: "right" }}>
                    <Typography
                      variant="body2"
                      style={{
                        color: "#94a3b8",
                        textDecoration: "line-through",
                        fontSize: "12px",
                      }}
                    >
                      ₹{prod.price}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      style={{ fontWeight: 800, color: "#0f172a" }}
                    >
                      ₹{prod.finalPrice}{" "}
                      <span
                        style={{
                          fontWeight: 400,
                          fontSize: "11px",
                          color: "#64748b",
                        }}
                      >
                        x{prod.quantity}
                      </span>
                    </Typography>
                  </div>
                </div>
              );
            })}
          </Paper>

          {/* CUSTOMER & REFUND INFO */}
          <Paper
            style={{
              padding: "24px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow: "none",
            }}
          >
            <Typography
              variant="h6"
              style={{
                fontWeight: 800,
                color: "#1e293b",
                marginBottom: "16px",
              }}
            >
              Customer Details & Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography
                  style={{
                    fontSize: "11px",
                    color: "#64748b",
                    fontWeight: 600,
                  }}
                >
                  CUSTOMER NAME
                </Typography>
                <Typography style={{ fontWeight: 700, color: "#0f172a" }}>
                  {order.shippingAddress?.fullName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  style={{
                    fontSize: "11px",
                    color: "#64748b",
                    fontWeight: 600,
                  }}
                >
                  PHONE NUMBER
                </Typography>
                <Typography style={{ fontWeight: 700, color: "#0f172a" }}>
                  {order.shippingAddress?.phone}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  style={{
                    fontSize: "11px",
                    color: "#64748b",
                    fontWeight: 600,
                  }}
                >
                  TRANSACTION ID
                </Typography>
                <Typography
                  style={{
                    fontWeight: 700,
                    color: "#0f172a",
                    fontSize: "12px",
                  }}
                >
                  {order.payment?.transactionId || "N/A"}
                </Typography>
              </Grid>
            </Grid>

            {order.payment?.refundStatus && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "12px",
                  backgroundColor: "#fff1f2",
                  borderRadius: "8px",
                  border: "1px solid #fecdd3",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <Typography
                  variant="body2"
                  style={{ color: "#9f1239", fontWeight: 600 }}
                >
                  Refund Status:{" "}
                  <b>{order.payment.refundStatus.toUpperCase()}</b> (₹
                  {order.payment.refundAmount})
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  style={{ textTransform: "none", fontWeight: 600 }}
                  onClick={() => navigate("/admin/refunds")}
                >
                  Manage Refunds
                </Button>
              </div>
            )}
          </Paper>
        </Grid>

        {/* RIGHT COLUMN: FULFILLMENT & BILLING SUMMARY */}
        <Grid item xs={12} lg={4}>
          {/* SHIPMENT / FULFILLMENT CONSOLE */}
          <Paper
            style={{
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow: "none",
              marginBottom: "20px",
            }}
          >
            <Typography
              variant="subtitle2"
              style={{
                fontWeight: 800,
                color: "#334155",
                marginBottom: "12px",
              }}
            >
              📦 Shiprocket Fulfillment
            </Typography>

            {order.shiprocket?.awbCode ? (
              <div
                style={{
                  backgroundColor: "#f0fdf4",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #bbf7d0",
                }}
              >
                <Typography
                  variant="body2"
                  style={{ lineHeight: "1.6", fontSize: "13px" }}
                >
                  <b>Shipment ID:</b> {order.shiprocket.orderId}
                  <br />
                  <b>AWB Code:</b>{" "}
                  <span style={{ color: "#166534", fontWeight: 700 }}>
                    {order.shiprocket.awbCode}
                  </span>
                  <br />
                  <b>Courier:</b> {order.shiprocket.courierName || "Assigned"}
                </Typography>
              </div>
            ) : (
              <Box component="form" onSubmit={handleCreateShipment}>
                <Typography
                  variant="caption"
                  style={{
                    color: "#64748b",
                    display: "block",
                    marginBottom: "10px",
                  }}
                >
                  Enter package dimensions to generate AWB & Label.
                </Typography>

                <TextField
                  label="Weight (KG)"
                  type="number"
                  name="weight"
                  value={shipData.weight}
                  onChange={handleShipDataChange}
                  fullWidth
                  size="small"
                  required
                  inputProps={{ step: "0.01" }}
                  style={{ marginBottom: "10px" }}
                />

                <Grid container spacing={1} style={{ marginBottom: "12px" }}>
                  <Grid item xs={4}>
                    <TextField
                      label="L (cm)"
                      type="number"
                      name="length"
                      value={shipData.length}
                      onChange={handleShipDataChange}
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="B (cm)"
                      type="number"
                      name="breadth"
                      value={shipData.breadth}
                      onChange={handleShipDataChange}
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="H (cm)"
                      type="number"
                      name="height"
                      value={shipData.height}
                      onChange={handleShipDataChange}
                      size="small"
                      required
                    />
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isShipping}
                  style={{
                    backgroundColor: "#2563eb",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  {isShipping ? "Processing..." : "Generate Shipment"}
                </Button>
              </Box>
            )}
          </Paper>

          {/* BILLING / TOTALS SUMMARY */}
          <Paper
            style={{
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow: "none",
              marginBottom: "20px",
            }}
          >
            <Typography
              variant="subtitle2"
              style={{
                fontWeight: 800,
                color: "#334155",
                marginBottom: "12px",
              }}
            >
              💰 Order Totals
            </Typography>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
                fontSize: "13px",
              }}
            >
              <span style={{ color: "#64748b" }}>Subtotal</span>
              <span style={{ fontWeight: 600 }}>
                ₹{order.totals?.subTotal || 0}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
                fontSize: "13px",
              }}
            >
              <span style={{ color: "#ef4444" }}>Discount</span>
              <span style={{ fontWeight: 600, color: "#ef4444" }}>
                -₹{order.totals?.discount || 0}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
                fontSize: "13px",
              }}
            >
              <span style={{ color: "#64748b" }}>Tax</span>
              <span style={{ fontWeight: 600 }}>₹{order.totals?.tax || 0}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
                fontSize: "13px",
              }}
            >
              <span style={{ color: "#64748b" }}>Shipping Charge</span>
              <span style={{ fontWeight: 600 }}>
                ₹{order.totals?.shippingCharge || 0}
              </span>
            </div>
            <Divider style={{ margin: "12px 0" }} />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{ fontWeight: 800, color: "#0f172a", fontSize: "13px" }}
              >
                Grand Total:
              </span>
              <span
                style={{ fontWeight: 800, fontSize: "18px", color: "#10b981" }}
              >
                ₹{order.totals?.grandTotal || 0}
              </span>
            </div>
          </Paper>

          {/* SHIPPING ADDRESS */}
          <Paper
            style={{
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow: "none",
            }}
          >
            <Typography
              variant="subtitle2"
              style={{ fontWeight: 800, color: "#334155", marginBottom: "8px" }}
            >
              📍 Delivery Address
            </Typography>
            <Typography
              variant="body2"
              style={{ fontWeight: 700, color: "#0f172a" }}
            >
              {order.shippingAddress?.fullName}
            </Typography>
            <Typography
              variant="body2"
              style={{
                color: "#475569",
                fontSize: "12px",
                marginTop: "4px",
                lineHeight: "1.5",
              }}
            >
              {order.shippingAddress?.houseNumber},{" "}
              {order.shippingAddress?.area}
              <br />
              {order.shippingAddress?.city}, {order.shippingAddress?.state} -{" "}
              <b>{order.shippingAddress?.pincode}</b>
              <br />
              Country: {order.shippingAddress?.country || "India"}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default OrderDetailedAudit;
